import { useEffect, useRef } from 'react';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';

import { getSubMetricDetailQuery } from 'services/insights/useSubMetricDetail';
import { getTaxaOverTimeQuery } from 'services/insights/useTaxaOverTime';
import { getActionPlanItemsQuery } from 'services/actionPlan/actionPlanData';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { FeedbackPageType } from 'types/Feedback';
import { ActionPlanItem } from 'types/ActionPlan';
import { InsightMetric } from 'types/Insights';

import {
  PageHeader,
  ContainerSizes,
  PageContainer,
  LinkButton,
  ButtonSizes,
  ButtonColors,
  FeedbackWidget,
  ActionsCard,
} from 'components';
import { Icon, Typography } from '@repo/ui';
import { ActionPlanItemCard } from 'views/ActionPlan/ActionPlanGroupV2/ActionPlanGroupV2';
import { SubMetricData } from '../SubMetricData/SubMetricData';
import { MaturationIndexDetail } from './MaturationIndexDetail';
import { MATURATION_INDEX_CATEGORY_KEY } from 'views/Results/Summary/SummaryCards/MatIndexCard/MatIndexCard';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';

import { TinyAIPromptEntryPoint } from 'components/TinyAI/TinyAIPromptEntryPoint';
import styles from './SubMetricDetail.module.scss';

export const getActionsForSubMetric = (
  actions: ActionPlanItem[],
  subMetricId?: number | string,
) => {
  if (!subMetricId) return actions;
  const numericIdToUse =
    typeof subMetricId === 'string' ? parseInt(subMetricId) : subMetricId;
  return actions.filter((item: ActionPlanItem) =>
    item.desired_outcomes?.some(
      outcome =>
        outcome.section === 'Gut Vaginal Health' &&
        outcome.gut_vaginal_health &&
        outcome.gut_vaginal_health.id === numericIdToUse,
    ),
  );
};

export const InsightBreadcrumbs = ({
  subMetric,
}: {
  subMetric?: InsightMetric;
}) => {
  if (!subMetric) return <></>;
  return (
    <div className={styles.breadcrumbs}>
      <Typography variant='body-s'>
        {
          subMetric.gut_vaginal_health?.key_insights_category
            ?.key_insights_sections?.display_title
        }
      </Typography>
      <Typography variant='body-s'>
        ({subMetric?.gut_vaginal_health?.key_insights_category?.category_title})
      </Typography>
    </div>
  );
};

const relatedActionsEmptyStateText = {
  metric: 'No actions related to this metric',
  condition: 'No actions related to this condition',
};

export const RelatedActions = ({
  actions,
  isCondition,
}: {
  actions: ActionPlanItem[];
  isCondition?: boolean;
}) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  if (!currentKitId) {
    return null;
  }

  const emptyState = (
    <Typography variant='body-s'>
      {relatedActionsEmptyStateText[isCondition ? 'condition' : 'metric']}
    </Typography>
  );

  return (
    <ActionsCard
      title='Action Items'
      actions={actions}
      emptyState={emptyState}
      renderItem={(action, index) => (
        <ActionPlanItemCard
          key={index}
          action={action as ActionPlanItem}
          kitId={currentKitId}
        />
      )}
    >
      <LinkButton
        href={`/action-plan/${currentKitId}`}
        size={ButtonSizes.M}
        color={ButtonColors.PURPLE}
        label='Go to action plan'
        width={'full'}
        className='mt-4'
      />
    </ActionsCard>
  );
};

export const SubMetricDetail = () => {
  const { kitId, subMetricId } = useParams<{
    kitId: string;
    subMetricId: string;
  }>();
  const { state } = useLocation();
  const [{ data: subMetric }, { data: allActions }] = useSuspenseQueries({
    queries: [
      getSubMetricDetailQuery(kitId, subMetricId),
      getActionPlanItemsQuery(kitId),
      getTaxaOverTimeQuery(kitId, parseInt(subMetricId ?? '')),
    ],
  });
  const startAPRef = useRef(null);
  const actionItems = getActionsForSubMetric(allActions, subMetricId);

  useEffect(() => {
    if (state?.scrollTo === 'actions') {
      if (startAPRef.current) {
        const offset = (startAPRef.current as HTMLElement).offsetTop - 10;
        const scrollContainer = document.querySelector('#appWrapper');
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: offset,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [state]);

  if (subMetric.category_key === MATURATION_INDEX_CATEGORY_KEY) {
    //For any maturation index, we want to show the MaturationIndexDetail component instead
    return <MaturationIndexDetail />;
  }

  return (
    <PageContainer size={ContainerSizes.MD} className='pt-3'>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [subMetricId ?? '']: subMetric?.name,
        }}
      />
      <PageHeader
        title={subMetric?.name}
        beta={subMetric?.gut_vaginal_health?.beta}
      />
      <InsightBreadcrumbs subMetric={subMetric} />
      <TinyAIPromptEntryPoint location='metric_chatbot' variant='banner' />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <SubMetricData
            metric={subMetric}
            relatedActionsQty={actionItems?.length ?? 0}
            refAP={startAPRef}
          />
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <section id='actions' ref={startAPRef}>
            <RelatedActions actions={actionItems} />
            {!!kitId && subMetric.gut_vaginal_health.id && (
              <div className={'mt-4 hide-on-desktop'}>
                <FeedbackWidget
                  kitId={kitId}
                  pageType={FeedbackPageType.INSIGHT}
                  pageId={subMetric.gut_vaginal_health.id}
                />
              </div>
            )}
          </section>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
